import {
  getData,
  convertToWidthCharacter,
  filterBySearchString,
  getDataByInputText
} from '@/utils'
import { clone, cloneDeep, find } from 'lodash'
import { del } from 'vue'

// Master fields getDropDownOptions can search on the server
const SERVER_SEARCHABLE_DROPDOWN_FIELDS = ['variety', 'shipper', 'buyer']

const initColumns = (disable = []) => {
  const columns = [
    {
      name: 'cutpot',
      items: [
        {
          name: 'Cut',
          value: 'cut'
        },
        {
          name: 'Pot',
          value: 'pot'
        },
        {
          name: 'Other',
          value: 'other'
        }
      ],
      dropdown: true,
      isActive: true,
      rules: 'required|oneOf:cut,pot,other'
    },
    {
      name: 'icon',
      items: [
        {
          name: 'New',
          value: 'new'
        },
        {
          name: 'Special offer',
          value: 'special_offer'
        },
        {
          name: 'Recommended',
          value: 'recommended'
        },
        {
          name: 'Rare',
          value: 'rare'
        }
      ],
      dropdown: true,
      isActive: true,
      rules: 'oneOf:new,special_offer,recommended,rare'
    },
    {
      name: 'origin',
      isActive: true
    },
    {
      name: 'family',
      header: true,
      items: [
        {
          code: '001',
          name: 'product 1',
          value: 'product 1'
        },
        {
          code: '002',
          name: 'product 2',
          value: 'product 2'
        },
        {
          code: '003',
          name: 'product 3',
          value: 'product 3'
        },
        {
          code: '004',
          name: 'product 4',
          value: 'product 4'
        }
      ],
      dropdown: true,
      isActive: true,
      required: true,
      rules: 'required'
    },
    {
      name: 'variety',
      isActive: true,
      required: true,
      rules: 'required'
    },
    {
      name: 'color',
      isActive: true
    },
    {
      name: 'pot_size',
      isActive: true
    },
    {
      name: 'fee',
      isActive: true
    },
    {
      name: 'grade',
      isActive: true,
      help: 'The flower quality (Selected, Standard, etc...), or consistent length of stem, size of bloom.'
    },
    {
      name: 'quantity',
      isActive: true,
      required: true,
      rules: 'required',
      type: 'number',
      help: 'Number of units in each box'
    },
    {
      name: 'unit',
      isActive: true,
      help: 'E.g: stem, branch, etc.',
      rules: 'required',
      required: true
    },
    {
      name: 'pieces_per_unit',
      isActive: true,
      help: 'Number of pieces per each unit. E.g. stems per bunch',
      type: 'number',
      rules: 'min_value:1'
    },
    {
      name: 'box_weight',
      isActive: true,
      type: 'number'
    },
    {
      name: 'price',
      isActive: true,
      required: true,
      rules: 'required|greater_than_zero',
      help: 'Price per unit'
    },
    {
      name: 'note',
      isActive: true,
      help: 'Additional information for an item'
    },
    {
      name: 'private_note',
      isActive: true,
      help: 'Note for internal use, not visible to merchants. The note will also show up in orders.'
    },
    {
      name: 'sold_out',
      isActive: true,
      rules: 'isBool'
    },
    {
      name: 'images',
      isActive: true
    }
  ]

  columns.forEach((col) => {
    col.isActive = disable.indexOf(col.name) === -1
  })

  return columns
}

const initRow = (columns, data = null, page = undefined) => {
  // columns: string[]
  let row = {}
  columns.forEach((name) => {
    row[name] = null
  })
  if (data) {
    Object.keys(data).forEach((key) => {
      row[key] = data[key]
    })
  }
  // if (page === 'arrival') row = { ...row, redBucket: [] }
  return [row, { redBucket: [] }]
}

const checkLastRow = (rows, indexCurrentRowInput) => {
  return rows.length - 1 === indexCurrentRowInput
}

const autoAddEmptyRow = (rows, indexCurrentRowInput) => {
  if (checkLastRow(rows, indexCurrentRowInput)) return addOneRow(rows)
}

const addOneRow = (rows, index = null, data = null) => {
  /*
    {
        row: $table.rows[cursor.rowIndex],
        rowIndex: cursor.rowIndex,
        column: $table.columns[cursor.columnIndex],
        columnIndex: cursor.columnIndex,
        rows,
        rowIndexes,
        columns,
        columnIndexes,
    }
    */
  // exp: { color: 'red', box_type: 'L' }
  const row = rows[0]
  const column = Object.keys(row)
  let newRow = initRow(column, cloneDeep(data))[0]

  const indexInsert = index ? index : rows.length
  if (index === 0) {
    rows.splice(index, 0, newRow)
  } else {
    rows.splice(indexInsert, 0, newRow)
  }
  return rows
}

const addRows = (
  rows,
  numberRowAdd = 1,
  index = null,
  isAbove = false,
  data = null
) => {
  const indexInsert =
    index !== null ? (isAbove ? index : index + 1) : rows.length - 1
  for (let i = 0; i < numberRowAdd; i++) {
    rows = addOneRow(rows, indexInsert, data)
  }
  return rows
}

const deleteRows = (rows, numberRowDelete, index) => {
  if (rows.length === numberRowDelete + index) {
    rows = addOneRow(rows, numberRowDelete + index + 1)
  }
  const deletedRows = rows.slice(index, numberRowDelete + index)
  rows.splice(index, numberRowDelete)
  return [rows, deletedRows]
}

const copyRows = (
  rows,
  newRows,
  index,
  columns = [],
  notCopyProps = [],
  includeDisabled = false
) => {
  let indexInsert = index + 1
  let skipCopyProps = columns
    .filter((col) => col.skipCopy)
    .map((col) => col.name)
  skipCopyProps = [...notCopyProps, ...skipCopyProps, 'history']

  const disabledCols = columns
    .filter((col) => col.disabled)
    .map((col) => col.name)

  // not copy these props and disabled columns
  if (!includeDisabled) {
    skipCopyProps = [...skipCopyProps, ...disabledCols]
  }
  // debugger
  newRows.forEach((newRow) => {
    newRow = JSON.parse(JSON.stringify(newRow))
    // let skipCopyProps = [
    //   'history',
    //   'reg_no',
    //   'mixinfo',
    //   'mix_details',
    //   'sales',
    //   'purchase_amount',
    //   'purchase_unit_price',
    //   'readonly',
    //   'disabledRow',
    //   'desired_unit_price_from',
    //   'desired_unit_price_to',
    //   'is_web_request',
    //   'classes'
    // ]

    skipCopyProps.forEach((prop) => {
      if (prop in newRow) {
        newRow[prop] = null
      }
    })
    if (newRow.id) newRow.id = null
    newRow.is_tracking_no_check = false
    rows.splice(indexInsert, 0, newRow)
    indexInsert++
  })
  return rows
}

const getDropDownOptions = async (dataTable, masterField, params = {}) => {
  let searchInput = dataTable.searchInput?.trim().normalize('NFKC')
  // if (searchInput && searchInput.includes(':')) {
  //   const parts = searchInput.split(':')
  //   searchInput = parts[0] || null
  // }
  searchInput = convertToWidthCharacter(searchInput, 'half')
  const { page, per_page, ...restParams } = params
  const curColumn = dataTable.column.name
  let dropdownItems = []

  // process for column variety
  if (curColumn === 'variety') {
    const queryParams = {
      ...restParams,
      page: page || 1,
      per_page: per_page || 100
    }
    // if (dataTable.row?.item_id && !queryParams.item_id) {
    //   queryParams.item_id = dataTable.row.item_id
    // }
    const data = await getData([masterField], {
      variety: {
        searchstr: searchInput ? searchInput : '',
        active: true,
        ...queryParams
      }
    })
    dropdownItems = data.varieties
    dropdownItems._total = data.total.varieties
  } else if (curColumn === 'shipper') {
    const queryParams = {
      ...restParams,
      page: page || 1,
      per_page: per_page || 100
    }
    const data = await getData(['shipper'], {
      shipper: {
        searchstr: searchInput ? searchInput : '',
        active: true,
        ...queryParams
        // add default page and per_page to prevent crash when get all shippers
      }
    })
    dropdownItems = data.shippers
    dropdownItems._total = data.total.shippers
  } else if (curColumn === 'buyer') {
    const queryParams = {
      ...restParams,
      page: page || 1,
      per_page: per_page || 100
    }
    const data = await getData(['buyer'], {
      buyer: {
        searchstr: searchInput ? searchInput : '',
        active: true,
        ...queryParams
      }
    })
    dropdownItems = data.buyers
  }

  return dropdownItems
}

const formatDropdownLabel = (item) =>
  item.branch_code
    ? `${item.code}-${item.branch_code}: ${item.name}`
    : `${item.code}: ${item.name}`

const findExactDropdownItem = (items, text) =>
  find(items, (item) => formatDropdownLabel(item) === text) || null

// Only meaningful for items whose `code` alone is a unique key (no
// branch_code). If more than one item shares the pasted code we can't tell
// which was meant, so we don't guess.
const findDropdownItemByCode = (items, code) => {
  const matches = items.filter(
    (item) => !item.branch_code && String(item.code) === code
  )
  return matches.length === 1 ? matches[0] : null
}

// Pasting into a multi-row range fires one matchDropdownItemOnInput call per
// row without waiting for the previous one to finish (see FEditableTable's
// onPaste), so concurrent calls for the same column race on `column.items`
// and on overlapping server searches. Queue behind whichever call for this
// column is already in flight so pastes resolve one row at a time - the
// same guarantee a synchronous match gets for free.
const pendingDropdownMatchByColumn = new WeakMap()

// Paste sets the whole cell value at once (no progressive typing), so unlike
// live typing it must match a master record exactly rather than fuzzily.
// It also bypasses the autocomplete's live search, so `column.items` (only
// filled while a user types) may not yet contain the pasted value even
// though it exists in the master — fall back to a server search.
const matchDropdownItemOnInput = async (dataInput, column) => {
  const colItems = column?.items
  if (!colItems) return null
  if (!dataInput.onPaste) return getDataByInputText(dataInput.value, colItems)

  const previous = pendingDropdownMatchByColumn.get(column) || Promise.resolve()
  const current = previous
    .catch(() => undefined)
    .then(() => matchPastedDropdownItem(dataInput, column))
  pendingDropdownMatchByColumn.set(column, current)
  return current
}

const matchPastedDropdownItem = async (dataInput, column) => {
  const pastedText = dataInput.value?.trim()

  // A code-only paste ("1261") matches a real master record but not its
  // display format ("1261: 品名"). Normalize the cell to that format so the
  // row isn't just un-flagged but also saves correctly (save-time matching
  // elsewhere compares against this exact "code: name" string).
  const applyCodeMatch = (item) => {
    if (!item) return null
    if (dataInput.row) dataInput.row[column.name] = formatDropdownLabel(item)
    return item
  }

  let matchedItem = findExactDropdownItem(column.items, pastedText)
  if (matchedItem) return matchedItem
  matchedItem = findDropdownItemByCode(column.items, pastedText)
  if (matchedItem) return applyCodeMatch(matchedItem)
  if (!SERVER_SEARCHABLE_DROPDOWN_FIELDS.includes(column.name)) return null

  const fetchedItems = await getDropDownOptions(
    { searchInput: pastedText, column: { name: column.name } },
    column.name,
    { page: 1, per_page: 100 }
  )
  if (!fetchedItems?.length) return null
  const existingIds = new Set(column.items.map((item) => item.id))
  const newItems = fetchedItems.filter((item) => !existingIds.has(item.id))
  if (newItems.length) column.items = [...column.items, ...newItems]

  matchedItem = findExactDropdownItem(fetchedItems, pastedText)
  if (matchedItem) return matchedItem
  return applyCodeMatch(findDropdownItemByCode(fetchedItems, pastedText))
}

const getDropdownData = (columns, dropDownData) => {
  return columns.map((column) => {
    if (column.dropdown && dropDownData[column.name]) {
      column.items = dropDownData[column.name]
    }
    return { ...column }
  })
}

const isNotEmptyRow = (row) => {
  const cloneRow = JSON.parse(JSON.stringify(row))
  delete cloneRow.id
  delete cloneRow.is_direct
  delete cloneRow.is_delete
  delete cloneRow.disabled
  // delete cloneRow.is_communication_fees
  delete cloneRow.redBucket
  delete cloneRow.readonly
  delete cloneRow.disabledBucket
  delete cloneRow.odrnum
  return Object.keys(cloneRow).some((key) => {
    if (key === '$isValidate') return false
    return cloneRow[key] !== null && cloneRow[key] !== ''
  })
}

const validateRow = (row, column, value, allowValueZero = false) => {
  row = JSON.parse(JSON.stringify(row))
  if (!row.$isValidate) row.$isValidate = {}
  if (column.required) {
    if (column.type === 'number') {
      // Convert empty string to null
      value =
        typeof value === 'string'
          ? value
            ? parseFloat(value.normalize('NFKD'))
            : null
          : value
      if (value === null) {
        row.$isValidate[column.name] = true
        return [false, row]
      }

      // If is not allow zero, value must be !== 0
      if (!allowValueZero && !value) {
        row.$isValidate[column.name] = true
        return [false, row]
      }
    }
    if (allowValueZero && (value === '' || value === null)) {
      if (!row.$isValidate) row.$isValidate = {}
      row.$isValidate[column.name] = true
      return [false, row]
    }
    if (!allowValueZero && !value) {
      if (!row.$isValidate) row.$isValidate = {}
      row.$isValidate[column.name] = true
      return [false, row]
    }
  }
  return [true, row]
}

const validateRows = (rows, columns, allowValueZero = false, isPass = false) => {
  let success = true
  rows = rows.map((row) => {
    if (row.$isValidate && Object.keys(row.$isValidate).length && !isPass) {
      success = false
      return row
    }
    if (!isNotEmptyRow(row)) return row
    columns.forEach((column) => {
      const [successRow, rowResult] = validateRow(
        row,
        column,
        row[column.name],
        allowValueZero || column?.allowValueZero
      )
      success = success && successRow
      row = rowResult
    })
    return row
  })
  return [success, JSON.parse(JSON.stringify(rows))]
}

const insertIntoRedBucket = (rows, rowIdx, colName) => {
  if (!rows[rowIdx].redBucket) rows[rowIdx].redBucket = []
  const idx = rows[rowIdx].redBucket.indexOf(colName)
  if (idx === -1) rows[rowIdx].redBucket.push(colName)
}

const removeFromRedBucket = (rows, rowIdx, colName) => {
  if (!rows[rowIdx].redBucket) rows[rowIdx].redBucket = []
  const idx = rows[rowIdx].redBucket.indexOf(colName)
  if (idx !== -1) rows[rowIdx].redBucket.splice(idx, 1)
}

const matchingLazyDropdown = (dataInput, currCursor, columns, rowData) => {
  const colName = dataInput.column.name
  const colIdx = columns.findIndex((col) => col.name === colName)
  const rowIdx = rowData.indexOf(dataInput.row)
  const dropdownItems = columns[colIdx].items
  if (currCursor?.columnIndex === colIdx && currCursor?.rowIndex === rowIdx) {
    return
  }
  const normalizeText = convertToWidthCharacter(
    dataInput.searchInput?.normalize('NFKC'),
    'half'
  ).toLowerCase()
  const matchItem = dropdownItems.find((item) =>
    filterBySearchString(item, normalizeText)
  )
  if (matchItem) {
    rowData[rowIdx][colName] = `${matchItem.code}: ${matchItem.name}` // display {code}:{name}
    removeFromRedBucket(rowData, rowIdx, colName)
    return
  }
}

const mapDropdownFieldToId = (row, dropdownFields, tableCols) => {
  const newRow = JSON.parse(JSON.stringify(row))
  dropdownFields.forEach(({ field, idField, txtField }) => {
    if (row[field] !== undefined && row[field] !== null) {
      if (row[field] === '') {
        newRow[idField] = null
        delete newRow[txtField]
        delete newRow[field]
        return
      }
      // remove ${field} and ${field_txt} if already have ${field_id} to avoid conflict
      // if (newRow[idField]) {
      //   delete newRow[txtField]
      //   delete newRow[field]
      //   return
      // }

      const fieldCol = tableCols.find((col) => col.name === field)
      if (fieldCol && fieldCol.items) {
        const matchedItem = find(fieldCol.items, (item) =>
          item.branch_code
            ? `${item.code}-${item.branch_code}: ${item.name}` === row[field]
            : `${item.code}: ${item.name}` === row[field]
        )
        if (matchedItem) {
          newRow[idField] = matchedItem.id
          delete newRow[txtField] // don't need text field after mapping to id
        } else {
          newRow[txtField] = row[field]
          delete newRow[idField]
        }
        delete newRow[field]
      }
    }
  })
  return newRow
}

const normalizePayload = (rows, tableCols, ignoreFields = []) => {
  const cloneRows = cloneDeep(rows)
  const ignoreSet = new Set(ignoreFields || [])
  tableCols.forEach((col) => {
    if (ignoreSet.has(col.name)) return
    // process number type column
    if (col.type === 'number') {
      cloneRows.forEach((row) => {
        if (row[col.name] === '') {
          row[col.name] = null
        }
      })
    }
    if (col.type === 'checkbox') {
      cloneRows.forEach((row) => {
        row[col.name] = !!row[col.name]
      })
    }
  })
  return cloneRows
}

const calculateProfitPercent = (revenue, cost, decimals = null) => {
  if (revenue === null || cost === null || revenue === 0) return null
  let profit = revenue - cost
  let percent = (profit / revenue) * 100

  if (decimals !== null) {
    return Number(percent.toFixed(decimals))
  }

  return percent
}

const updateColumns = (columns, updates) => {
  return columns.map((col) => {
    const update = updates.find((u) => u.name === col.name)
    if (update) {
      return { ...col, ...update }
    }
    return col
  })
}

export {
  initRow,
  addOneRow,
  addRows,
  deleteRows,
  copyRows,
  autoAddEmptyRow,
  initColumns,
  getDropdownData,
  getDropDownOptions,
  matchDropdownItemOnInput,
  isNotEmptyRow,
  validateRow,
  validateRows,
  insertIntoRedBucket,
  removeFromRedBucket,
  matchingLazyDropdown,
  mapDropdownFieldToId,
  normalizePayload,
  calculateProfitPercent,
  updateColumns
}
